import type { FunctionComponent } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { POTENTIAL_BANDS, potentialBandTranslationKey } from '../model/automation';
import { usePalette } from './PaletteProvider';

/** Stacked share of hours per expected band, None → Very high, with a surface gap between segments. */
export const BandDistributionBar: FunctionComponent<{
    readonly className?: string;
    readonly distribution: readonly number[];
}> = ({ className, distribution }) => {
    const translate = useTranslate();
    const { bandColor } = usePalette();
    const present = POTENTIAL_BANDS.map((band, index) => ({ band, share: distribution[index] ?? 0 })).filter(
        ({ share }) => share > 0,
    );
    const label = present.map(({ band }) => translate(potentialBandTranslationKey(band))).join(' · ');

    return (
        <Tooltip delayDuration={120}>
            <Tooltip.Trigger asChild={true}>
                <div
                    aria-label={`${translate('pages.ai-transformation.item-tile.task-mix')}: ${label}`}
                    className={`flex h-2.5 w-full gap-px overflow-hidden rounded-xs ${className ?? ''}`}
                    role="img"
                >
                    {present.map(({ band, share }) => (
                        <span
                            key={band}
                            aria-hidden={true}
                            className="block h-full min-w-0 shrink-0"
                            style={{ backgroundColor: bandColor(band), flexBasis: `${share * 100}%` }}
                        />
                    ))}
                </div>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="top">
                    <ul className="m-0 flex list-none flex-col gap-xxs p-0">
                        {present.map(({ band }) => (
                            <li key={band} className="flex items-center gap-s">
                                <span
                                    aria-hidden={true}
                                    className="size-2 shrink-0 rounded-full"
                                    style={{ backgroundColor: bandColor(band) }}
                                />
                                {translate(potentialBandTranslationKey(band))}
                            </li>
                        ))}
                    </ul>
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};
